import { AxiosError } from 'axios';

export const snakeToCamel = (str: string): string => {
  return str.replace(/([-_][a-z])/gi, s => {
    return s.toUpperCase().replace('-', '').replace('_', '');
  });
};

export const flattanArrayOfObjectsToKeyValue = <T>(
  arr: { [key: string]: T }[]
) =>
  arr.reduce((acc, item) => {
    return { ...item, ...acc };
  }, {});

const isObject = (item: unknown) => {
  return item && typeof item === 'object' && !Array.isArray(item);
};

export const deepCompare = (a: unknown, b: unknown): boolean => {
  /* Compare arrays */
  if (Array.isArray(a) && Array.isArray(b)) {
    const arrayA = [...(a as unknown[])];
    const arrayB = [...(b as unknown[])];

    return deepCompareArray(arrayA, arrayB);
  }

  /* Compare objects */
  if (isObject(a) && isObject(b)) {
    const objectA = { ...(a as Record<string, unknown>) };
    const objectB = { ...(b as Record<string, unknown>) };

    return deepCompareObject(objectA, objectB);
  }

  /* Normal comparison... */
  if (a === b) {
    return true;
  }

  return false;
};

export const deepCompareObject = (
  a: Record<string, unknown>,
  b: Record<string, unknown>
): boolean => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) {
    return false;
  }

  for (const key of aKeys) {
    if (!bKeys.includes(key)) {
      return false;
    }

    if (!deepCompare(a[key], b[key])) {
      return false;
    }
  }

  return true;
};

export const deepCompareArray = (a: unknown[], b: unknown[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    if (!deepCompare(a[i], b[i])) {
      return false;
    }
  }

  return true;
};

/* Some utils functions that may be moved when new local logging is implemented */
export const isValidError = (error: unknown): error is Error | AxiosError => {
  return error instanceof Error;
};

export const isEmpty = (extraData: Record<string, unknown>) => {
  return Object.keys(extraData).length === 0;
};

export const defaultSensitiveData = [
  'Authorization',
  'password',
  'accessToken',
  'refreshToken',
  'base64',
];

type Data = Record<string, unknown> | Record<string, unknown>[];

/* recersive hides sensitive data from objects or arrays of objects */
export const hideSensitiveData = (
  data: Data,
  keys: string[] = defaultSensitiveData
): Data => {
  if (Array.isArray(data)) {
    return data.map((item: Data) => ({
      ...hideSensitiveData(item, keys),
    }));
  }
  if (!isObject(data)) return data;
  const copyOfData = { ...data };
  Object.keys(copyOfData).forEach(prop => {
    if (copyOfData[prop] === Object(copyOfData[prop])) {
      copyOfData[prop] = hideSensitiveData(copyOfData[prop] as Data, keys);
    } else if (keys.includes(prop)) copyOfData[prop] = 'HIDDEN';
  });
  return copyOfData;
};

/* Recursive minimize data only 3 deep objects */
export const minifyData = (data: Data): Data => {
  if (Array.isArray(data)) {
    return data.map((item: Data) => ({
      ...minifyData(item),
    }));
  }
  if (!isObject(data) || isEmpty(data)) return data;
  return Object.fromEntries(Object.entries(data).slice(0, 3));
};

export const isNetworkError = (error: string | undefined) =>
  error === AxiosError.ERR_NETWORK;

export const fileToArrayBuffer = async (file: File) => {
  return await file.arrayBuffer();
};

export const arrayBufferToFile = (
  arrayBuffer: ArrayBuffer,
  fileName = 'image.jpg'
): File => {
  const blob = new Blob([arrayBuffer], { type: 'image/jpeg' });
  return new File([blob], fileName, { type: 'image/jpeg' });
};

export const windowExists = () => typeof window !== 'undefined';

export const isMobileDevice = () => {
  return windowExists() && window.innerWidth < 768;
};
